import { useField } from 'formik';
import { useQuery } from '@apollo/client';

import Combobox, { ComboboxProps } from '#/components/controls/Combobox';
import Field from '#/components/forms/Field';
import { GetCustomersDocument } from '#/__generated__/graphql/graphql';
import { isRequiredYupField, useYupRequiredFields } from '#/components/forms/Formik';

export const CustomerCombobox: React.FC<CustomerComboboxProps> = ({ value, ...props }) => {
  const { data, loading } = useQuery(GetCustomersDocument);

  return (
    <Combobox
      {...props}
      displayValue={(item) => item?.node?.name}
      getKey={(item) => item?.node?.id}
      isLoading={props.isLoading ?? loading}
      items={data?.customers?.edges || []}
      value={value}
    >
      {(item) => (
        <Combobox.Option key={item.node.id} value={item}>
          {item.node.name}
        </Combobox.Option>
      )}
    </Combobox>
  );
};
export type CustomerComboboxProps = Omit<
  ComboboxProps,
  'children' | 'displayValue' | 'items' | 'getKey'
>;

export const CustomerComboboxFormik: React.FC<CustomerComboboxFormikProps> = ({
  invalid,
  label,
  name,
  ...props
}) => {
  const [field, meta, helper] = useField(name);
  const requiredFields = useYupRequiredFields();
  const isRequired = isRequiredYupField(requiredFields, name);
  const showError = meta.touched && !!meta.error;

  return (
    <Field name={name}>
      {label && (
        <label className="uppercase mb-1 inline-block" htmlFor={`customer-combobox-${name}`}>
          {label} {isRequired && <span className="text-red-600">*</span>}
        </label>
      )}
      <CustomerCombobox
        {...field}
        size="lg"
        {...props}
        id={name}
        invalid={invalid ?? showError}
        onChange={helper.setValue}
        required={props.required ?? isRequired}
      />
    </Field>
  );
};
export type CustomerComboboxFormikProps = CustomerComboboxProps & {
  label?: string;
  name: string;
};

export default CustomerCombobox;
